import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Eye, EyeOff, Pencil, Trash2, Plus } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { Card, PageTitle, TextInput, TextArea, CheckInput, Btn, run } from "../../components/admin/AdminUI";

const EMPTY = { name: "", location: "", vehicle: "", route: "", text: "", visible: true };

const ReviewForm = ({ initial, onSave, onCancel, testid }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3" data-testid={testid}>
      <div className="grid sm:grid-cols-2 gap-3">
        <TextInput label="First name" value={f.name} onChange={set("name")} testid={`${testid}-name`} required />
        <TextInput label="City, State" value={f.location} onChange={set("location")} testid={`${testid}-location`} />
        <TextInput label="Vehicle / transport" value={f.vehicle} onChange={set("vehicle")} testid={`${testid}-vehicle`} />
        <TextInput label="Route" value={f.route} onChange={set("route")} testid={`${testid}-route`} />
      </div>
      <TextArea label="Review text" value={f.text} onChange={set("text")} rows={4} testid={`${testid}-text`} required />
      <CheckInput label="Visible on website" checked={f.visible} onChange={set("visible")} testid={`${testid}-visible`} />
      <div className="flex gap-2">
        <Btn type="submit" data-testid={`${testid}-save`}>Save review</Btn>
        {onCancel && <Btn type="button" variant="ghost" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Btn>}
      </div>
    </form>
  );
};

const ReviewRow = ({ r, i, count, onMove, onToggle, onEdit, onDelete }) => {
  const [confirm, setConfirm] = useState(false);
  return (
    <article className={`panel p-5 ${r.visible ? "" : "opacity-60"}`} data-testid={`review-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900" data-testid={`review-${i}-name`}>{r.name}</span>
            <span className="text-xs text-slate-500">{r.location}</span>
            {!r.visible && <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5" data-testid={`review-${i}-hidden`}>Hidden</span>}
          </div>
          <div className="mt-1 text-xs text-slate-500">{[r.vehicle, r.route].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="flex gap-1">
          <Btn variant="ghost" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`review-${i}-up`} aria-label="Move up"><ArrowUp className="w-4 h-4" /></Btn>
          <Btn variant="ghost" disabled={i === count - 1} onClick={() => onMove(i, 1)} data-testid={`review-${i}-down`} aria-label="Move down"><ArrowDown className="w-4 h-4" /></Btn>
          <Btn variant="ghost" onClick={() => onToggle(r)} data-testid={`review-${i}-toggle`}>{r.visible ? <><EyeOff className="w-4 h-4" />Hide</> : <><Eye className="w-4 h-4" />Show</>}</Btn>
          <Btn variant="ghost" onClick={() => onEdit(r.id)} data-testid={`review-${i}-edit`}><Pencil className="w-4 h-4" />Edit</Btn>
          {confirm
            ? <Btn variant="danger" onClick={() => onDelete(r)} data-testid={`review-${i}-delete-confirm`}>Confirm delete</Btn>
            : <Btn variant="ghost" onClick={() => setConfirm(true)} data-testid={`review-${i}-delete`}><Trash2 className="w-4 h-4" /></Btn>}
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-800 whitespace-pre-wrap">"{r.text}"</p>
    </article>
  );
};

export default function Reviews() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => adminApi.get("/admin/reviews").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (f) => { if (await run(() => adminApi.post("/admin/reviews", f), "Review added")) { setAdding(false); load(); } };
  const update = async (f) => { if (await run(() => adminApi.put(`/admin/reviews/${f.id}`, f), "Review saved")) { setEditing(null); load(); } };
  const toggle = async (r) => { if (await run(() => adminApi.patch(`/admin/reviews/${r.id}/visibility`, { visible: !r.visible }))) load(); };
  const remove = async (r) => { if (await run(() => adminApi.delete(`/admin/reviews/${r.id}`), "Review deleted")) load(); };
  const move = async (i, d) => {
    const ids = items.map((r) => r.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    const res = await run(() => adminApi.put("/admin/reviews/order", { ids }));
    if (res) setItems(res.data);
  };

  return (
    <div data-testid="admin-reviews">
      <PageTitle eyebrow="Website" title="Reviews" actions={!adding && <Btn onClick={() => setAdding(true)} data-testid="review-add-button"><Plus className="w-4 h-4" />Add review</Btn>} />
      <p className="text-sm text-slate-600 mb-5 max-w-2xl">Visible reviews appear on the home page in this order. Replace the samples with real customer reviews as they come in.</p>
      {adding && <Card title="New review" className="mb-5"><ReviewForm initial={EMPTY} onSave={create} onCancel={() => setAdding(false)} testid="review-new" /></Card>}
      {!items ? <p className="text-slate-500">Loading…</p> : items.length === 0 ? <Card><p className="text-sm text-slate-500" data-testid="reviews-empty">No reviews yet. The home page hides the section until you add one.</p></Card> : (
        <div className="space-y-3" data-testid="reviews-list">
          {items.map((r, i) => editing === r.id
            ? <Card key={r.id} title="Edit review"><ReviewForm initial={r} onSave={update} onCancel={() => setEditing(null)} testid={`review-edit-${i}`} /></Card>
            : <ReviewRow key={r.id} r={r} i={i} count={items.length} onMove={move} onToggle={toggle} onEdit={setEditing} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
